import {MultiAxisDimSelection, MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {
  computeExpandedSelectionBox,
  computeHasRealSelection,
  computeIsEveryColumnSelected,
  computeIsEveryRowSelected,
  computeMultiAxisDimSelection,
  computeSelectionBox,
} from 'notebook/tables/store/logic/TableSelectionLogic';
import {createTableStoreSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore, TableEditableVolatileProps} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';

export interface TableSelectionModel {
  /**
   * The normalized selection box with min/max bounds.
   * Returns null if there is no selection.
   */
  selectionBox: MultiSelectionBox | null;

  /**
   * The selection box expanded to fully contain any intersecting merged regions.
   * When the selection includes ghost cells (outside table bounds), returns the raw
   * normalized box without merge expansion.
   * Returns null if there is no selection.
   */
  expandedBox: MultiSelectionBox | null;

  /**
   * The full-row and full-column index ranges for the current selection.
   * A full row is selected when the selection spans all displayed columns (respecting slice boundaries).
   * A full column is selected when the selection spans all rows.
   */
  multiAxisDimSelection: MultiAxisDimSelection;

  /**
   * Whether the selection covers every row of the table, which (because a row axis selection
   * requires every column to be selected) means the whole table is selected. Consumed by the row
   * axis cell to offer "Delete table" instead of "Delete rows".
   */
  isEveryRowSelected: boolean;

  /**
   * Whether the selection covers every column of the table, which (because a column axis selection
   * requires every row to be selected) means the whole table is selected. Consumed by the column
   * axis cell to offer "Delete table" instead of "Delete columns".
   */
  isEveryColumnSelected: boolean;

  /**
   * Whether there is a real (valid) selection within the table bounds.
   */
  hasRealSelection: boolean;

  /**
   * Whether there is any multi-selection (both start and end indices are non-null).
   */
  hasMultiSelection: boolean;

  /**
   * The multi-select key for the table, used for multi-selection tracking.
   */
  multiSelectKey: string;
}

function createSelectionBoxSelector(): (store: TableEditableStore) => MultiSelectionBox | null {
  return createTableStoreSelector(
    'selectionBox',
    {
      multiStartIndex: (store) => store.state.multiStartIndex,
      multiEndIndex: (store) => store.state.multiEndIndex,
    },
    ({multiStartIndex, multiEndIndex}) => computeSelectionBox(multiStartIndex, multiEndIndex)
  );
}

function createExpandedBoxSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps
): (store: TableEditableStore) => MultiSelectionBox | null {
  return createTableStoreSelector(
    'expandedBox',
    {
      multiStartIndex: (store) => store.state.multiStartIndex,
      multiEndIndex: (store) => store.state.multiEndIndex,
      table: (store) => volatilePropsSelector(store).table,
      computedTable: (store) => volatilePropsSelector(store).computedTable,
    },
    ({multiStartIndex, multiEndIndex, table, computedTable}) =>
      computeExpandedSelectionBox(multiStartIndex, multiEndIndex, table, computedTable)
  );
}

function createMultiAxisDimSelectionSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps,
  expandedBoxSelector: (store: TableEditableStore) => MultiSelectionBox | null,
  singleInputBoundaryIndexSelector: (store: TableEditableStore) => number | null
): (store: TableEditableStore) => MultiAxisDimSelection {
  return createTableStoreSelector(
    'multiAxisDimSelection',
    {
      expandedBox: expandedBoxSelector,
      table: (store) => volatilePropsSelector(store).table,
      singleInputBoundaryIndex: singleInputBoundaryIndexSelector,
    },
    ({expandedBox, table, singleInputBoundaryIndex}) => {
      const tableMaxRowIndex = table.table.length - 1;
      const tableMaxColIndex = table.table[0].length - 1;
      const sliceStart = singleInputBoundaryIndex ?? 0;
      const sliceEnd = tableMaxColIndex;
      return computeMultiAxisDimSelection(expandedBox, tableMaxRowIndex, sliceStart, sliceEnd);
    }
  );
}

export function createTableSelectionSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps,
  singleInputBoundaryIndexSelector: (store: TableEditableStore) => number | null
): (store: TableEditableStore) => TableSelectionModel {
  const expandedBoxSelector = createExpandedBoxSelector(volatilePropsSelector);
  const multiAxisDimSelectionSelector = createMultiAxisDimSelectionSelector(
    volatilePropsSelector,
    expandedBoxSelector,
    singleInputBoundaryIndexSelector
  );

  return createTableStoreSelector(
    'tableSelection',
    {
      selectionBox: createSelectionBoxSelector(),
      expandedBox: expandedBoxSelector,
      multiAxisDimSelection: multiAxisDimSelectionSelector,
      table: (store) => volatilePropsSelector(store).table,
      multiStartIndex: (store) => store.state.multiStartIndex,
      multiEndIndex: (store) => store.state.multiEndIndex,
      cid: (store) => store.stableProps.cid,
    },
    ({selectionBox, expandedBox, multiAxisDimSelection, table, multiStartIndex, multiEndIndex, cid}) => ({
      selectionBox,
      expandedBox,
      multiAxisDimSelection,
      isEveryRowSelected: computeIsEveryRowSelected(multiAxisDimSelection, table.table.length - 1),
      isEveryColumnSelected: computeIsEveryColumnSelected(multiAxisDimSelection, table.table[0].length - 1),
      hasRealSelection: computeHasRealSelection(selectionBox, table),
      hasMultiSelection: multiStartIndex != null && multiEndIndex != null,
      multiSelectKey: TableUtil.getTableMultiSelectKey(cid),
    })
  );
}
